import type { CreditTransactionDTO } from '../../../Generated/CreditTransactionDTO.ts'
import { DateWidget } from '../../Common/DateWidget.js'

interface TransactionHistoryProps {
  transactions: CreditTransactionDTO[]
  onReload?: () => void
  reloading?: boolean
}

const TYPE_BADGE: Record<string, { label: string; className: string }> = {
  PURCHASE: { label: 'Purchase', className: 'badge-success' },
  CONSUMPTION: { label: 'Consumption', className: 'badge-warning' },
  ADJUSTMENT: { label: 'Adjustment', className: 'badge-info' },
  EXPIRY: { label: 'Expiry', className: 'badge-error' }
}

export const TransactionHistory = ({ transactions, onReload, reloading = false }: TransactionHistoryProps) => {
  const handleReload = () => onReload?.()

  return (
    <div data-testid="transaction-history" className="bg-slate-100 rounded-lg p-6 drop-shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold">Transaction History</h2>
        {onReload && (
          <button className="btn btn-ghost btn-sm btn-square" onClick={handleReload} disabled={reloading} title="Refresh transactions">
            {reloading ? (
              <span className="loading loading-spinner loading-sm" />
            ) : (
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
            )}
          </button>
        )}
      </div>
      {transactions.length === 0 ? (
        <p className="text-gray-500 text-sm py-4">No transactions yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="table table-sm">
            <thead>
              <tr>
                <th>Date</th>
                <th>Type</th>
                <th className="text-right">Amount</th>
                <th className="text-right">Balance After</th>
                <th>Note</th>
              </tr>
            </thead>
            <tbody>
              {transactions.map((tx, i) => {
                const badgeInfo = TYPE_BADGE[tx.transactionType] ?? { label: tx.transactionType, className: 'badge-ghost' }
                const isPositive = tx.amount > 0

                return (
                  <tr key={`${tx.createdAt}-${tx.transactionType}-${i}`}>
                    <td className="text-sm whitespace-nowrap"><DateWidget date={tx.createdAt} /></td>
                    <td>
                      <span className={`badge badge-sm ${badgeInfo.className}`}>{badgeInfo.label}</span>
                    </td>
                    <td className={`text-right font-mono text-sm ${isPositive ? 'text-green-600' : 'text-red-500'}`}>
                      {isPositive ? '+' : ''}
                      {tx.amount}
                    </td>
                    <td className="text-right font-mono text-sm">{tx.balanceAfter}</td>
                    <td className="text-sm text-gray-500 max-w-48 truncate">{tx.note ?? ''}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
